/**
 * ChartCard Component
 *
 * Reusable wrapper for dashboard charts that handles:
 * - Consistent card structure with header and content
 * - Loading skeleton state
 * - Empty data state with customizable message
 * - Responsive height classes
 */

import type { ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';

/** Standard responsive height classes for charts */
export const CHART_HEIGHT_CLASSES = 'h-[200px] sm:h-[250px] md:h-[300px]';

/** Smaller height for compact charts like pie charts */
export const CHART_HEIGHT_COMPACT_CLASSES = 'h-[200px] sm:h-[250px]';

export interface ChartCardProps {
  /** Chart title displayed in the header */
  title: string;
  /** Optional tooltip explaining what this chart shows */
  tooltip?: string;
  /** Optional content to display in the header (e.g., summary stats) */
  headerContent?: ReactNode;
  /** Loading state - shows skeleton when true */
  isLoading: boolean;
  /** Whether the data is empty after loading */
  isEmpty: boolean;
  /** Custom message for empty state */
  emptyMessage?: string;
  /** aria-label for empty state */
  emptyAriaLabel?: string;
  /** The chart content to render */
  children: ReactNode;
  /** Use compact height for smaller charts */
  compact?: boolean;
}

export function ChartCard({
  title,
  tooltip,
  headerContent,
  isLoading,
  isEmpty,
  emptyMessage = 'No data available for the selected time range',
  emptyAriaLabel,
  children,
  compact = false,
}: ChartCardProps) {
  const heightClasses = compact
    ? CHART_HEIGHT_COMPACT_CLASSES
    : CHART_HEIGHT_CLASSES;

  return (
    <Card className="min-w-0 overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-1">
          <CardTitle>{title}</CardTitle>
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex cursor-help"
                  aria-label={`Learn more about ${title}`}
                >
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        {!isLoading && headerContent}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className={`${heightClasses} w-full`} />
        ) : isEmpty ? (
          <div
            className={`${heightClasses} flex items-center justify-center text-muted-foreground`}
            role="status"
            aria-label={
              emptyAriaLabel || `No ${title.toLowerCase()} data available`
            }
          >
            {emptyMessage}
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}
